

import React, { useState, useEffect } from 'react';
import { Pane, TextInputField, Button, Alert,Text, Tablist, Tab, Spinner, Heading, SelectField } from 'evergreen-ui';
import FirestoreService, {AccountManagerService} from '../appservices/AppManager'; // Ensure correct import path
import useStore from '../appservices/store';
import { useNavigate } from 'react-router-dom';
import { auth, firestore } from '../appservices/firebase'; // Import the updated Firebase configuration
import { onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail, updateProfile } from 'firebase/auth'; // Firebase Auth functions
import { doc, setDoc } from 'firebase/firestore'; // Firestore functions for document management
import axios from 'axios'; // Axios for making HTTP requests
import { waitFor } from '@testing-library/react';

import { useLocation } from 'react-router-dom';

 
const SignupComponent= ({ selectedPlan }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    businessName: '',
    businessEmail: '',
    phone: '',
    address: '',
    industry: '',
    customerVolume: '',
    role: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [userCreated, setUserCreated] = useState(false); // Tracks if Step 1 is complete
  const navigate = useNavigate();
  const { setUser, setBusinessId, setDatastoreId, setOnboardComplete } = useStore();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleAccountCreation = async () => {
    setIsLoading(true);
    try {
      // Step 1: Create user in Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(auth, formData.businessEmail, formData.password);
      const user = userCredential.user;

      // Step 2: Update user profile with display name
      await updateProfile(user, { displayName: `${formData.firstName} ${formData.lastName}` });

      setUser(user); // Update Zustand store
      setUserCreated(true); // Mark Step 1 as complete
    } catch (err) {
      console.error('Account creation error:', err);
      setError(err.message || 'Account creation failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinalizeRegistration = async () => {
    setIsLoading(true);
    try {
      const isBusinessAccount = selectedPlan === 'business';

      const userData = {
        uid: auth.currentUser?.uid, // Firebase UID
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: isBusinessAccount ? formData.role : 'home',
        settings: {}, // Add default or custom settings if needed
        ...(isBusinessAccount && {
          businessName: formData.businessName,
          businessEmail: formData.businessEmail,
          phone: formData.phone,
          address: formData.address,
          industry: formData.industry,
          purpose: 'AI support',
          userData: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
          },
        }),
      };

      // Call backend to initialize user account
      const accountData = await AccountManagerService.createUserAccount(userData);
      console.log('Account creation successful:', accountData);

      // Update Zustand store with businessId and datastoreId if returned
      if (accountData.businessAccount?.businessId) {
        setBusinessId(accountData.businessAccount.businessId);
      }
      if (accountData.businessAccount?.datastoreId) {
        setDatastoreId(accountData.businessAccount.datastoreId);
      }

      setOnboardComplete(true); // Mark onboarding as complete
      navigate('/dashboard'); // Redirect to dashboard
    } catch (err) {
      console.error('Registration finalization error:', err);
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Pane padding={32} background="white" borderRadius={8} width={600} maxWidth="100%">
      <Heading size={800} marginBottom={16}>
        {selectedPlan === 'home' ? 'Home Plan Registration' : 'Business Plan Registration'}
      </Heading>

      {error && <Alert intent="danger" title={error} marginBottom={16} />}
      {isLoading && (
        <Pane display="flex" justifyContent="center" alignItems="center" marginBottom={16}>
          <Spinner />
        </Pane>
      )}

      {!userCreated ? (
        selectedPlan === 'home' ? (
          <HomePlanRegistration
            formData={formData}
            onInputChange={handleInputChange}
            onAccountCreation={handleAccountCreation}
          />
        ) : (
          <BusinessPlanRegistration
            formData={formData}
            onInputChange={handleInputChange}
            onAccountCreation={handleAccountCreation}
          />
        )
      ) : (
        selectedPlan === 'home' ? (
          <HomePlanRegistration
            formData={formData}
            onInputChange={handleInputChange}
            onFinalizeRegistration={handleFinalizeRegistration}
          />
        ) : (
          <BusinessPlanRegistration
            formData={formData}
            onInputChange={handleInputChange}
            onFinalizeRegistration={handleFinalizeRegistration}
          />
        )
      )}
    </Pane>
  );
};




// Home Plan Registration Component
const HomePlanRegistration = ({ formData, onInputChange }) => (
  <Pane display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={16} marginBottom={24}>
    <TextInputField
      label="First Name"
      name="firstName"
      value={formData.firstName}
      onChange={onInputChange}
    />
    <TextInputField
      label="Last Name"
      name="lastName"
      value={formData.lastName}
      onChange={onInputChange}
    />
    <TextInputField
      label="Email"
      name="email"
      value={formData.email}
      onChange={onInputChange}
    />
    <TextInputField
      label="Password"
      name="password"
      type="password"
      value={formData.password}
      onChange={onInputChange}
    />
    <TextInputField
      label="Phone Number"
      name="phone"
      value={formData.phone}
      onChange={onInputChange}
    />
    <TextInputField
      label="Address"
      name="address"
      value={formData.address}
      onChange={onInputChange}
    />
  </Pane>
);

 
const BusinessPlanRegistration = ({
  formData,
  onInputChange,
  onAccountCreation,
  onFinalizeRegistration,
  userCreated,
}) => {
  const [step, setStep] = useState(1); // Step 1 or Step 2
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);


  const validateStep = () => {
    if (step === 1) {
      const requiredFields = ['firstName', 'lastName', 'businessName', 'businessEmail', 'phone', 'password'];
      for (const field of requiredFields) {
        if (!formData[field]) {
          setError(`Please fill out the ${field.replace(/([A-Z])/g, ' $1').toLowerCase()} field.`);
          return false;
        }
      }
    } else if (step === 2) {
      const requiredFields = ['address', 'industry', 'customerVolume'];
      for (const field of requiredFields) {
        if (!formData[field]) {
          setError(`Please fill out the ${field.replace(/([A-Z])/g, ' $1').toLowerCase()} field.`);
          return false;
        }
      }
    }

    setError('');
    return true;
  };

  const handleNextStep = async () => {
    if (validateStep()) {
      if (step === 1) {
        console.log('Step 1 data:', formData);
        await onAccountCreation(); // Call account creation logic
      }
      setStep(step + 1);
    }
  };

  const handlePreviousStep = () => {
    setStep(step - 1);
  };

  const handleFinalize = () => {
    if (validateStep()) {
      onFinalizeRegistration(); // Call final registration logic
    }
  };

  return (
    <Pane padding={16}>
      <Heading size={700} marginBottom={16}>
        {step === 1 ? 'Step 1: Basic Info & Authentication' : 'Step 2: Business Details'}
      </Heading>

      {error && <Alert intent="danger" title={error} marginBottom={16} />}

      {step === 1 && (
        <Pane display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={16} marginBottom={24}>
          <TextInputField
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={onInputChange}
          />
          <TextInputField
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={onInputChange}
          />
          <TextInputField
            label="Role"
            name="role"
            value={formData.role}
            onChange={onInputChange}
            placeholder="e.g., Manager, Owner"
          />
          <TextInputField
            label="Business Name"
            name="businessName"
            value={formData.businessName}
            onChange={onInputChange}
          />
          <TextInputField
            label="Business Email"
            name="businessEmail"
            value={formData.businessEmail}
            onChange={onInputChange}
          />
          <TextInputField
            label="Phone Number"
            name="phone"
            value={formData.phone}
            onChange={onInputChange}
          />
          <TextInputField
            label="Password"
            type="password"
            name="password"
            value={formData.password}
            onChange={onInputChange}
          />
        </Pane>
      )}

      {step === 2 &&  (
        <Pane display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={16} marginBottom={24}>
          <TextInputField
            label="Business Address"
            name="address"
            value={formData.address}
            onChange={onInputChange}
          />
          <TextInputField
            label="Industry"
            name="industry"
            value={formData.industry}
            onChange={onInputChange}
          />
          <TextInputField
            label="Customer Volume"
            name="customerVolume"
            value={formData.customerVolume}
            onChange={onInputChange}
            placeholder="e.g., 500+"
          />
        </Pane>
      )}

      <Pane display="flex" justifyContent="space-between" marginTop={16}>
        {step === 2 && (
          <Button appearance="default" onClick={handlePreviousStep}>
            Back
          </Button>
        )}
        {step === 1 ? (
          <Button appearance="primary" onClick={handleNextStep} isLoading={isLoading}>
            Next
          </Button>
        ) : (
          <Button appearance="primary" onClick={handleFinalize} isLoading={isLoading}>
            Complete Registration
          </Button>
        )}
      </Pane>
    </Pane>
  );
};



const AuthComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  // const selectedPlan = queryParams.get('plan'); // Get the selected plan ('home' or 'business')
  const selectedPlan = "business"


  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState('signup'); // 'signup', 'login', 'reset'
  const { user, setUser, subscriptionStatus, onboardComplete } = useStore();

  useEffect(() => {
    if (user) {
      if (subscriptionStatus === 'active' && onboardComplete) {
        // Navigate to the dashboard if payment is completed and onboarding is complete
        navigate('/dashboard');
      } else if (subscriptionStatus !== 'active' && onboardComplete) {
        // Redirect to checkout if payment is pending
        navigate('/checkout');
      }  
    }
  }, [user, subscriptionStatus, onboardComplete, navigate]);
  

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      setUser(userCredential.user); // Update Zustand store with user info
      navigate('/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to login. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!formData.email) {
      setError('Please enter your email address.');
      return;
    }
    setIsLoading(true);
    try {
      await sendPasswordResetEmail(auth, formData.email);
      setError('Password reset email sent. Check your inbox.');
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Pane display="flex" alignItems="center" justifyContent="center" height="100vh">
      <Pane width={600} padding={32} background="white" borderRadius={8}>
        <Heading size={800} marginBottom={16}>
          {view === 'signup'
            ? selectedPlan === 'home'
              ? 'Home Plan Registration'
              : 'Business Plan Registration'
            : view === 'login'
            ? 'Login to Your Account'
            : 'Reset Your Password'}
        </Heading>

        {error && <Alert intent="danger" title={error} marginBottom={16} />}
        {isLoading && (
          <Pane display="flex" justifyContent="center" marginBottom={16}>
            <Spinner />
          </Pane>
        )}

        {view === 'signup' && (
          <SignupComponent selectedPlan={selectedPlan} />
        )}

        {view === 'login' && (
          <>
            <TextInputField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
            <TextInputField
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              required
            />
            <Button appearance="primary" onClick={handleLogin} width="100%" marginTop={16}>
              Login
            </Button>
          </>
        )}

        {view === 'reset' && (
          <>
            <TextInputField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
            <Button appearance="primary" onClick={handleResetPassword} width="100%" marginTop={16}>
              Reset Password
            </Button>
          </>
        )}

        <Pane marginTop={16} textAlign="center">
          {view === 'signup' && (
            <>
              <Text size={300} color="muted">
                Already have an account?{' '}
                <Text
                  size={300}
                  color="blue"
                  onClick={() => setView('login')}
                  style={{ cursor: 'pointer' }}
                >
                  Login
                </Text>
              </Text>
            </>
          )}

          {view === 'login' && (
            <>
              <Text size={300} color="muted">
                Forgot your password?{' '}
                <Text
                  size={300}
                  color="blue"
                  onClick={() => setView('reset')}
                  style={{ cursor: 'pointer' }}
                >
                  Reset Password
                </Text>
              </Text>
              <br />
              <Text size={300} color="muted">
                Don't have an account?{' '}
                <Text
                  size={300}
                  color="blue"
                  onClick={() => setView('signup')}
                  style={{ cursor: 'pointer' }}
                >
                  Sign Up
                </Text>
              </Text>
            </>
          )}

          {view === 'reset' && (
            <>
              <Text size={300} color="muted">
                Remember your password?{' '}
                <Text
                  size={300}
                  color="blue"
                  onClick={() => setView('login')}
                  style={{ cursor: 'pointer' }}
                >
                  Login
                </Text>
              </Text>
            </>
          )}
        </Pane>
      </Pane>
    </Pane>
  );
};




 





const AuthView = () => {
  return (
    <Pane display="flex" height="100vh" flexDirection="row" background="tint2">
      {/* Left Column - Tagline */}
      <Pane
        flex={1}
        display="flex"
        alignItems="center"
        justifyContent="center"
        padding={32}
        background="white"
      >
        <Pane textAlign="center"> {/* Centering content within the column */}
          <Heading size={900} marginBottom={8}>
            24/7
          </Heading>
          <Heading size={900} color="muted">
            Product Support Simplified.
          </Heading>
        </Pane>
      </Pane>

      {/* Right Column - Authentication Form */}
      <Pane
        flex={1}
        display="flex"
        alignItems="center"
        justifyContent="center"
        background="tint2"
        padding={32}
      >
        {/* Use imported AuthComponent */}
        <AuthComponent />
      </Pane>
    </Pane>
  );
};


export default AuthView;